/**
 * 核心概念 — the conceptual spine of the Analects.
 *
 * Every bank item and every passage is tagged with one or more of these, so
 * progress can be reported as "how well do you understand 仁 / 禮 / 義利",
 * not merely "how many sentences have you seen". `keyPassages` are the
 * canonical loci a student should be able to cite for the concept.
 */
import type { VirtueId } from "../engine/types";

export type ConceptId =
  | "ren"
  | "li"
  | "xue"
  | "junzi"
  | "xiao"
  | "shu"
  | "xin"
  | "yili"
  | "zheng"
  | "zhengming"
  | "zhiren"
  | "zhongyong"
  | "tianming"
  | "wenzhi"
  | "shijiao"
  | "chushi"
  | "jiaoxue";

export interface Concept {
  id: ConceptId;
  label: string;
  glyph: string;
  virtue: VirtueId;
  /** One line a student should be able to say back. */
  gist: string;
  /** The question this concept exists to answer — shown as the map node's subtitle. */
  question: string;
  /** The distinction most often got wrong. */
  pitfall: string;
  keyPassages: number[];
}

export const CONCEPTS: Concept[] = [
  {
    id: "ren",
    label: "仁",
    glyph: "仁",
    virtue: "ren",
    gist: "仁是待人的根本用心：克己、愛人、推己及人，且必須落在具體行事上。",
    question: "什麼樣的用心才配稱作仁？",
    pitfall: "把仁當成一團和氣、誰都不得罪；孔子說「唯仁者能好人，能惡人」，仁反而有明確的是非。",
    keyPassages: [43, 67, 68, 69, 70, 127, 150, 180, 323, 324, 344, 429, 456, 482],
  },
  {
    id: "li",
    label: "禮",
    glyph: "禮",
    virtue: "li",
    gist: "禮是行為的分寸與共同秩序，根在敬，不在排場。",
    question: "行為的邊界由什麼決定？",
    pitfall: "把禮等同於儀式排場；孔子說「禮云禮云，玉帛云乎哉」，玉帛鐘鼓都不是禮的本身。",
    keyPassages: [12, 43, 44, 59, 79, 147, 190, 212, 323, 487, 541],
  },
  {
    id: "xue",
    label: "學",
    glyph: "學",
    virtue: "zhi",
    gist: "學是「聞—習—思—改」的循環，未落實到行為的都還不算學會。",
    question: "怎樣才算真的學會了？",
    pitfall: "把學當成聽過、記住；「學而不思則罔，思而不學則殆」，兩頭都不能缺。",
    keyPassages: [1, 14, 27, 31, 33, 123, 140, 151, 158, 170, 172, 205, 400, 451, 471, 518, 519],
  },
  {
    id: "junzi",
    label: "君子",
    glyph: "君",
    virtue: "yong",
    gist: "君子是德行標準而非身份地位，靠行義、求諸己、不黨不爭來辨認。",
    question: "怎麼判斷一個人是不是君子？",
    pitfall: "把君子讀成貴族身份；《論語》裡君子小人之分幾乎全是德行之分。",
    keyPassages: [8, 28, 30, 77, 82, 138, 187, 369, 372, 399, 403, 438, 441, 442],
  },
  {
    id: "xiao",
    label: "孝",
    glyph: "孝",
    virtue: "ren",
    gist: "孝的核心是敬與長久的用心，不是供養，也不是無條件順從。",
    question: "孝與單純的奉養差在哪裡？",
    pitfall: "把孝讀成事事順從；「事父母幾諫」明說該勸就要勸，只是要敬、要不怨。",
    keyPassages: [2, 11, 21, 22, 23, 24, 84, 85, 86, 87, 497, 530],
  },
  {
    id: "shu",
    label: "忠恕",
    glyph: "恕",
    virtue: "ren",
    gist: "一以貫之的方法：盡己為忠，推己及人為恕，「己所不欲，勿施於人」。",
    question: "有沒有一條可以終身奉行的準則？",
    pitfall: "把恕當成寬容原諒；恕是以己度人的推衍方法，不等於一味放過。",
    keyPassages: [4, 81, 104, 324, 435, 444],
  },
  {
    id: "xin",
    label: "信",
    glyph: "信",
    virtue: "xin",
    gist: "信是言行可以兌現，是人與人、政與民之間的承重結構。",
    question: "為什麼失信比缺糧更嚴重？",
    pitfall: "把信理解成有諾必踐；「信近於義，言可復也」，先看該不該諾，再談守不守。",
    keyPassages: [5, 7, 13, 38, 329, 426, 523],
  },
  {
    id: "yili",
    label: "義利",
    glyph: "義",
    virtue: "yong",
    gist: "取捨時先問該不該（義），再問合不合算（利）；富貴不以其道則不處。",
    question: "面對好處，先問什麼？",
    pitfall: "把義利讀成排斥財富；孔子不反對富貴，只反對「不以其道得之」。",
    keyPassages: [40, 71, 76, 78, 82, 162, 166, 499, 514],
  },
  {
    id: "zheng",
    label: "為政",
    glyph: "政",
    virtue: "li",
    gist: "為政以德：治理者先正己，用德禮養出羞恥心，而不是只靠刑罰壓住。",
    question: "靠刑罰治理，缺了什麼？",
    pitfall: "把德治讀成不要法度；「道之以政，齊之以刑」不是不能用，而是「民免而無恥」，不夠。",
    keyPassages: [17, 19, 35, 36, 329, 339, 341, 347, 352, 359, 425, 540],
  },
  {
    id: "zhengming",
    label: "正名",
    glyph: "名",
    virtue: "li",
    gist: "名分不正，言語就失據，事情就辦不成；秩序從名實相符開始。",
    question: "為政為何要從「正名」下手？",
    pitfall: "把正名讀成糾正稱呼；它指的是名分與實質相符，君要像君、臣要像臣。",
    keyPassages: [41, 145, 333, 349],
  },
  {
    id: "zhiren",
    label: "知人",
    glyph: "知",
    virtue: "zhi",
    gist: "識人要看動機、來路與所安，並且不因眾人毀譽而免於自己查證。",
    question: "怎樣才不會看錯一個人？",
    pitfall: "憑一句話或一時表現斷人；「不以言舉人，不以人廢言」是兩面都要防。",
    keyPassages: [16, 26, 73, 328, 344, 370, 443, 448, 466, 541],
  },
  {
    id: "zhongyong",
    label: "中庸",
    glyph: "中",
    virtue: "zhi",
    gist: "中庸是隨事求恰好，過與不及同樣是失；和而不同，不是折衷湊合。",
    question: "「過猶不及」為什麼過也算錯？",
    pitfall: "把中庸讀成和稀泥；「鄉愿，德之賊也」正是孔子最厭惡的假中庸。",
    keyPassages: [12, 149, 286, 367, 369, 489, 539],
  },
  {
    id: "tianming",
    label: "天命",
    glyph: "命",
    virtue: "zhi",
    gist: "盡人事而知有不可強求者；敬鬼神而遠之，把力氣放回人事上。",
    question: "承認有命，會不會就變成認命？",
    pitfall: "把知命讀成消極認命；孔子知其不可而為之，知命是安於結果，不是放棄行動。",
    keyPassages: [20, 130, 142, 171, 173, 185, 282, 327, 411, 470, 541],
  },
  {
    id: "wenzhi",
    label: "文質",
    glyph: "文",
    virtue: "li",
    gist: "質是底子，文是修飾，兩者相稱才是君子；缺一則野或史。",
    question: "內在樸實與外在修飾，哪個可以省？",
    pitfall: "把「文」當虛偽外表而主張全去掉；子貢說「文猶質也，質猶文也」，去掉一邊就分不出虎豹犬羊。",
    keyPassages: [48, 54, 138, 330],
  },
  {
    id: "shijiao",
    label: "詩教",
    glyph: "詩",
    virtue: "zhi",
    gist: "《詩》能興、觀、群、怨，是感發與表達的訓練，不是背誦材料。",
    question: "讀詩到底在練什麼能力？",
    pitfall: "把學詩當成積累辭句；「誦詩三百，授之以政不達」，不能用就是白讀。",
    keyPassages: [15, 18, 48, 60, 65, 196, 351, 485, 486],
  },
  {
    id: "chushi",
    label: "出處",
    glyph: "隱",
    virtue: "yong",
    gist: "邦有道則見，無道則隱；進退要看時勢與自身是否守得住道。",
    question: "什麼時候該出仕，什麼時候該退？",
    pitfall: "把隱讀成怕事；孔子批評的是「邦有道，貧且賤焉，恥也」，該做事時躲開才叫恥。",
    keyPassages: [94, 113, 161, 201, 202, 222, 379, 413, 427, 504, 507, 508],
  },
  {
    id: "jiaoxue",
    label: "教學",
    glyph: "誨",
    virtue: "zhi",
    gist: "有教無類而因材施教：同一個問題，對不同的人給不同的答案。",
    question: "為什麼同樣的問題，孔子給的答案不一樣？",
    pitfall: "把不同答案讀成前後矛盾；那是針對各人的偏處下藥。",
    keyPassages: [141, 157, 158, 292, 459, 480, 525],
  },
];

export const CONCEPT_MAP = new Map(CONCEPTS.map((c) => [c.id, c]));

export function conceptById(id: ConceptId): Concept {
  const concept = CONCEPT_MAP.get(id);
  if (!concept) throw new Error(`unknown concept: ${id}`);
  return concept;
}
